import { ReactElement } from 'react'

import { TFunction } from 'i18next'
import { Translation } from 'react-i18next'

import { Flex, Icon } from 'components/core'
import { IconListType } from 'components/core/Icon/constants'
import { AdAccountLink } from 'components/Links'
import { TextDividerVertical } from 'components/TextDividerVertical'
import { getCountryNames } from 'helpers/countries'
import { getRegionNames } from 'helpers/regions'
import { Brand, CountryCode } from 'types'

import { DetailWrapper, DetailIcon, DetailText } from './shared'

export function adAccountRenderer(adAccount: Brand): ReactElement {
    const text = <AdAccountLink adAccount={adAccount} />
    return (
        <DetailWrapper
            leftContent={
                <DetailIcon
                    icon={() => <Icon name="SHOPPING_BAG" />}
                    tooltipTitle={
                        <Translation>
                            {(t) =>
                                t(
                                    'common:advertisingAccount',
                                    'Advertising Account'
                                )
                            }
                        </Translation>
                    }
                />
            }
            rightContent={<DetailText text={text} />}
        />
    )
}

export function adAccountRegionCountryRenderer(
    adAccount: Brand,
    t: TFunction
): ReactElement {
    const regions = getRegionNames(t)
    const expandedRegion =
        adAccount.region in regions
            ? regions[adAccount.region]
            : adAccount.region

    const tooltip = (
        <>
            {t('common:region', 'Region')}
            <TextDividerVertical />
            {t('common:country', 'Country')}
        </>
    )

    const text = (
        <Flex>
            {expandedRegion}
            <TextDividerVertical />
            <Flex gap={4}>
                <Icon
                    name={adAccount.country_code as IconListType}
                    height={15}
                    width={20}
                />
                <div>{adAccount.country_code}</div>
            </Flex>
        </Flex>
    )

    return (
        <DetailWrapper
            leftContent={
                <DetailIcon
                    icon={() => <Icon name="PIN" />}
                    tooltipTitle={tooltip}
                />
            }
            rightContent={<DetailText text={text} />}
        />
    )
}

export function adAccountNameMarketplaceRenderer(
    name: string,
    countryCode: CountryCode,
    t: TFunction
): ReactElement {
    const tooltip = (
        <>
            {t('common:brand', 'Brand')}
            <TextDividerVertical />
            {t('common:marketplace', 'Marketplace')}
        </>
    )

    const countries = getCountryNames(t)

    const text = (
        <Flex>
            {name}
            <TextDividerVertical />
            <Flex gap={4}>
                <Icon
                    name={countryCode as IconListType}
                    height={15}
                    width={20}
                />
                <div>{countries[countryCode]}</div>
            </Flex>
        </Flex>
    )

    return (
        <DetailWrapper
            leftContent={
                <DetailIcon
                    icon={() => <Icon name="SHOPPING_BAG" />}
                    tooltipTitle={tooltip}
                />
            }
            rightContent={<DetailText text={text} />}
        />
    )
}
